import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, Move, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "Memory in Code",
        icon: "🧠",
        content: (
            <div className="space-y-6">
                <div className="bg-green-50 p-6 rounded-2xl border-2 border-green-200">
                    <h3 className="font-bold text-xl text-green-800 mb-3">What is State?</h3>
                    <p className="text-slate-700 mb-4">
                        Ordinary variables "forget" their values when a function finishes.
                    </p>
                    <p className="text-slate-700">
                        <strong>State</strong> is special memory for components. It lets them "remember" things like a score, user input, or shopping cart items.
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "The Hook",
        icon: "🪝",
        content: (
            <div className="space-y-6">
                <div className="bg-slate-900 text-blue-300 p-4 rounded-xl font-mono text-sm">
                    <div><span className="text-purple-400">const</span> [count, setCount] = <span className="text-yellow-400">useState</span>(0);</div>
                </div>
                <ul className="space-y-4">
                    <li className="flex items-center gap-4 bg-white p-4 rounded-xl border border-slate-200">
                        <div className="bg-blue-100 text-blue-800 font-bold px-2 py-1 rounded font-mono">count</div>
                        <span className="text-slate-600 text-sm">The current value (starts at 0).</span>
                    </li>
                    <li className="flex items-center gap-4 bg-white p-4 rounded-xl border border-slate-200">
                        <div className="bg-green-100 text-green-800 font-bold px-2 py-1 rounded font-mono">setCount</div>
                        <span className="text-slate-600 text-sm">The function to update the value.</span>
                    </li>
                </ul>
            </div>
        )
    },
    {
        id: 3,
        title: "Re-rendering",
        icon: "🔄",
        content: (
            <div className="space-y-6">
                <div className="bg-orange-50 p-6 rounded-2xl border-2 border-orange-200">
                    <h3 className="font-bold text-xl text-orange-800 mb-3">Automatic Updates</h3>
                    <p className="text-slate-700 mb-4">
                        When you call <code>setCount(5)</code>, React automatically <strong>re-renders</strong> (refreshes) the component to show the new number.
                    </p>
                    <p className="font-bold text-orange-900">
                        Mission: Use the state buttons to get exactly 10 bananas in the cart.
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME COMPONENT ============
const StateGame = ({ onComplete }: { onComplete: () => void }) => {
    const [bananas, setBananas] = useState(0);

    const increment = () => setBananas(b => b + 1);
    const decrement = () => setBananas(b => Math.max(0, b - 1));
    const reset = () => setBananas(0);

    const checkWin = () => {
        if (bananas === 10) {
            onComplete();
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* Visualizer - The Rendered View */}
            <div className="bg-slate-800 p-8 rounded-3xl border border-slate-700 min-h-[400px] flex flex-col items-center justify-center relative">
                <div className="absolute top-4 left-4 text-xs font-bold text-slate-500 uppercase tracking-widest">
                    Browser Render
                </div>

                <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-white">Gorilla Mart Cart</h2>

                    <motion.div
                        key={bananas}
                        initial={{ scale: 1.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="text-8xl font-black text-white"
                    >
                        {bananas}
                    </motion.div>
                    <div className="text-lg text-slate-400 mt-2">Bananas Added</div>
                </div>

                <div className="flex gap-4">
                    <button onClick={decrement} className="p-4 rounded-full bg-slate-700 hover:bg-slate-600 transition-all text-white">
                        <Minus />
                    </button>
                    <button onClick={increment} className="p-4 rounded-full bg-green-500 hover:bg-green-400 transition-all text-white shadow-lg shadow-green-500/30">
                        <Plus />
                    </button>
                </div>

                <button onClick={reset} className="mt-8 text-xs text-slate-500 hover:text-white underline">
                    Reset State
                </button>
            </div>

            {/* Logic / Code */}
            <div className="space-y-6">
                <div className="bg-slate-950 p-6 rounded-2xl border border-slate-800 font-mono text-sm relative overflow-hidden group">
                    {/* Syntax Highlight imitation */}
                    <div className="text-purple-400">const [bananas, setBananas] = useState(0);</div>
                    <br />
                    <div className="text-blue-300">{'<div className="count">'}{bananas}{'</div>'}</div>
                    <br />
                    <div className="text-yellow-300">
                        {'<button onClick={() => setBananas(b => b + 1)} />'}
                    </div>

                    <motion.div
                        className="absolute inset-0 bg-green-500/10 pointer-events-none"
                        initial={{ opacity: 0 }}
                        key={`flash-${bananas}`}
                        animate={{ opacity: [0.5, 0] }}
                        transition={{ duration: 0.3 }}
                    />
                </div>

                <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700">
                    <h3 className="font-bold text-green-400 mb-2">Challenge</h3>
                    <p className="text-slate-300 mb-4">
                        The UI updates <em>instantly</em> when state changes.
                        <br /><br />
                        <strong>Goal:</strong> Get exactly 10 bananas in your cart to proceed.
                    </p>

                    {bananas === 10 && (
                        <div className="space-y-4 mt-6">
                            <motion.button
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                onClick={checkWin}
                                className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-400 text-white font-bold animate-pulse"
                            >
                                Checkout (10 Items) 🛒
                            </motion.button>
                            
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.5 }}
                            >
                                <Link to="/games/state-sorter" className="w-full py-3 rounded-xl bg-blue-500 hover:bg-blue-400 text-white font-bold flex items-center justify-center gap-2 transition-transform hover:scale-105 border-b-4 border-blue-700">
                                    🕹️ Play "State Sorter" Arcade
                                </Link>
                                <p className="text-center text-xs text-blue-400 mt-2 font-bold uppercase">Game Unlocked!</p>
                            </motion.div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const ReactState = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('react-state', 100);
    };

    return (
        <LessonWrapper
            moduleName="React State"
            moduleEmoji="🧠"
            moduleColor="green"
            lessons={LESSONS}
            completed={completed}
            activityLabel="State Machine"
        >
            <StateGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default ReactState;
